"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Input } from "components/ui/v2/input";
import {
  fieldVariantClasses,
  type FieldVariant,
} from "components/ui/v2/field-variants";
import { Z_INDEX } from "components/ui/v2/z-index";
import { Check, ChevronDown, Loader2, Search, X } from "lucide-react";
import * as React from "react";
import {
  normalizePickerData,
  resolvePickerLabel,
  type PickerRawItem,
} from "./picker-data-utils";

export interface TagPickerV2Props {
  data?: any[] | string[];
  items?: any[];
  value?: Array<string | number>;
  /** @deprecated rsuite compat — use value */
  defaultValue?: Array<string | number>;
  onChange?: (value: string[], event?: React.SyntheticEvent) => void;
  labelKey?: string;
  valueKey?: string;
  placeholder?: React.ReactNode;
  searchable?: boolean;
  cleanable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  block?: boolean;
  creatable?: boolean;
  className?: string;
  style?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  menuMaxHeight?: number;
  variant?: FieldVariant;
  id?: string;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  preventOverflow?: boolean;
  placement?: string;
  /** Tamaño del picker: "sm" | "filter" (w-64 h-11) | "md" | "lg". Default: "filter" */
  size?: "sm" | "filter" | "md" | "lg";
  menuAutoWidth?: boolean;
  menuClassName?: string;
  /** @deprecated rsuite compat — ignored */
  renderMenu?: (menu: React.ReactNode) => React.ReactNode;
  /** @deprecated rsuite compat — ignored or passthrough */
  renderMenuItemCheckbox?: boolean | ((checkboxProps: any) => React.ReactNode);
  renderMenuItem?: (
    label: React.ReactNode,
    item: any,
    selected?: boolean
  ) => React.ReactNode;
  renderValue?: (
    value: any,
    item: any,
    selectedElement: React.ReactNode
  ) => React.ReactNode;
  searchBy?: (
    keyword: string,
    label: React.ReactNode,
    item: any
  ) => boolean;
  /**
   * Cuando se pasa, el picker deja de filtrar `data` en el cliente — asume que
   * `data` ya viene filtrada por el caller (búsqueda remota) y llama a esto
   * (debounced, 350ms) cada vez que cambia el texto de búsqueda.
   */
  onSearchChange?: (query: string) => void;
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function toStringArray(value: Array<string | number> | undefined) {
  return (value ?? []).map(String);
}

/**
 * Multi-select nativo v2 — reemplaza el bridge rsuite TagPicker.
 */
export const TagPickerV2 = React.forwardRef<HTMLButtonElement, TagPickerV2Props>(
function TagPickerV2(
  {
  data,
  items,
  value,
  defaultValue,
  onChange,
  onBlur,
  labelKey = "label",
  valueKey = "value",
  placeholder = "Seleccionar...",
  searchable = true,
  cleanable = true,
  disabled = false,
  loading = false,
  block = false,
  creatable = false,
  className,
  style,
  menuStyle,
  menuMaxHeight = 256,
  variant = "slate",
  size = "filter",
  name: _name,
  id: _id,
  menuAutoWidth: _menuAutoWidth,
  menuClassName: _menuClassName,
  renderMenu: _renderMenu,
  renderMenuItemCheckbox: _renderMenuItemCheckbox,
  renderMenuItem,
  renderValue,
  searchBy,
  onSearchChange,
  },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout>>();

  const handleQueryChange = (next: string) => {
    setQuery(next);
    if (!onSearchChange) return;
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => onSearchChange(next), 350);
  };

  React.useEffect(() => {
    return () => clearTimeout(searchDebounceRef.current);
  }, []);
  const colors = fieldVariantClasses(variant);

  const sizeClasses = size === "filter" ? "w-64 h-11" : "h-11";

  const rawData = data || items || [];

  const resolvedValue = value ?? defaultValue;
  const selectedValues = React.useMemo(
    () => toStringArray(resolvedValue),
    [resolvedValue]
  );

  const options = React.useMemo(() => {
    const arr = rawData.map((item) => {
      if (typeof item === "string") {
        return { label: item, value: item };
      }
      return item;
    });
    return normalizePickerData(arr, labelKey, valueKey);
  }, [rawData, labelKey, valueKey]);

  const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);

  const filteredOptions = React.useMemo(() => {
    if (onSearchChange) return options;
    const q = normalize(query);
    if (!q || !searchable) return options;
    return options.filter((option) => {
      if (searchBy) {
        return searchBy(q, option.label, option.raw);
      }
      return normalize(option.label).includes(q);
    });
  }, [options, query, searchable, searchBy, onSearchChange]);

  const selectedLabels = React.useMemo(
    () =>
      selectedValues
        .map((id) => options.find((option) => option.value === id)?.label ?? id)
        .filter(Boolean),
    [options, selectedValues]
  );

  const triggerLabel = React.useMemo(() => {
    if (loading) return "Cargando...";
    if (selectedValues.length === 0) return String(placeholder);
    if (selectedValues.length === 1) return selectedLabels[0] ?? String(placeholder);
    if (selectedValues.length === 2) {
      const [a, b] = selectedLabels;
      return a && b ? `${a}, ${b}` : `${selectedValues.length} seleccionados`;
    }
    return `${selectedValues.length} seleccionados`;
  }, [loading, placeholder, selectedLabels, selectedValues.length]);

  const triggerContent = React.useMemo(() => {
    const defaultElement = (
      <span className="min-w-0 flex-1 truncate">{triggerLabel}</span>
    );

    if (renderValue && selectedValues.length > 0) {
      const rawItems = selectedValues
        .map((id) => options.find((option) => option.value === id)?.raw ?? id)
        .filter((item) => item !== undefined);
      return renderValue(selectedValues, rawItems, defaultElement);
    }

    return defaultElement;
  }, [options, renderValue, selectedValues, triggerLabel]);

  const toggleOption = (optionValue: string) => {
    onChange?.(
      selectedSet.has(optionValue)
        ? selectedValues.filter((id) => id !== optionValue)
        : [...selectedValues, optionValue]
    );
  };

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.([]);
  };

  const handleCreate = () => {
    const next = query.trim();
    if (!next || selectedSet.has(next)) return;
    onChange?.([...selectedValues, next]);
    setQuery("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const canCreate =
    creatable &&
    searchable &&
    query.trim().length > 0 &&
    !options.some((option) => normalize(option.label) === normalize(query)) &&
    !selectedSet.has(query.trim());

  const hasValue = selectedValues.length > 0;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn("relative my-1", sizeClasses, className)}
        style={style}
      >
        <PopoverPrimitive.Trigger asChild disabled={disabled || loading}>
          <button
            ref={ref}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            onBlur={onBlur}
            className={cn(
              "relative flex items-center rounded-2xl border bg-white/70 text-left shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 outline-none",
              size === "filter" ? "w-64" : "w-full",
              "hover:bg-white/90 dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)] dark:hover:bg-zinc-950/90",
              "focus-visible:ring-1 focus-visible:ring-offset-0",
              colors.border,
              colors.focusBorder,
              colors.focusRing,
              open && cn("ring-1 ring-offset-0", colors.focusRing.replace(/focus:/g, "")),
              "h-11 text-sm",
              hasValue ? "px-4 pr-16" : "px-4 pr-9",
              disabled && "cursor-not-allowed opacity-50",
              loading && "cursor-wait",
              !hasValue && "text-muted-foreground"
            )}
          >
            {triggerContent}

            {hasValue && !loading ? (
              <span className="absolute right-14 top-1/2 shrink-0 -translate-y-1/2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                {selectedValues.length}
              </span>
            ) : null}

            {loading ? (
              <Loader2 className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown
                className={cn(
                  "pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground transition-transform duration-200",
                  open && "rotate-180"
                )}
              />
            )}
          </button>
        </PopoverPrimitive.Trigger>

        {hasValue && cleanable && !loading && !disabled ? (
          <button
            type="button"
            aria-label="Limpiar"
            onClick={handleClear}
            className="absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={12}
          className={cn(
            "tp-panel z-[3000] w-[var(--radix-popover-trigger-width)] min-w-[14rem] overflow-hidden rounded-xl",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "duration-150"
          )}
          style={{ zIndex: Z_INDEX.MODAL + 100, ...menuStyle }}
        >
          {searchable ? (
            <div className="border-b border-border px-2 pt-2">
              <Input
                size="sm"
                variant={variant}
                label={String(placeholder)}
                value={query}
                onChange={(event) => handleQueryChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canCreate) {
                    event.preventDefault();
                    handleCreate();
                  }
                }}
                leftIcon={<Search className="h-3.5 w-3.5" />}
              />
            </div>
          ) : null}

          <div
            className="overflow-y-auto overscroll-contain p-1"
            role="listbox"
            aria-multiselectable
            style={{ maxHeight: menuMaxHeight }}
          >
            {canCreate ? (
              <button
                type="button"
                onClick={handleCreate}
                className="mb-1 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] font-medium text-primary transition-colors hover:bg-primary/10"
              >
                Agregar &ldquo;{query.trim()}&rdquo;
              </button>
            ) : null}

            {filteredOptions.length === 0 && !canCreate ? (
              <p className="px-2 py-4 text-center text-[11px] text-muted-foreground">
                Sin resultados
              </p>
            ) : (
              filteredOptions.map((option) => {
                const checked = selectedSet.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] leading-tight transition-colors",
                      "hover:bg-muted/80",
                      checked && "bg-primary/8"
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors",
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-background"
                      )}
                    >
                      {checked ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-foreground">
                      {resolvePickerLabel(option, renderMenuItem, checked)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>

      <style jsx global>{`
        .tp-panel {
          isolation: isolate;
          background:
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.96) 0%,
              rgba(255, 255, 255, 0.88) 100%
            ),
            rgba(248, 250, 252, 0.95);
          backdrop-filter: blur(16px) saturate(1.2);
          -webkit-backdrop-filter: blur(16px) saturate(1.2);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.6),
            0 4px 12px -2px rgba(15, 23, 42, 0.08),
            0 18px 40px -12px rgba(15, 23, 42, 0.14);
        }
        .dark .tp-panel {
          background:
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.08) 0%,
              rgba(255, 255, 255, 0.02) 100%
            ),
            rgba(20, 24, 40, 0.94);
          backdrop-filter: blur(16px) saturate(1.15);
          -webkit-backdrop-filter: blur(16px) saturate(1.15);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.1),
            0 8px 24px -4px rgba(0, 0, 0, 0.45);
        }
      `}</style>
    </PopoverPrimitive.Root>
  );
});

TagPickerV2.displayName = "TagPickerV2";
